/**
 * React component for building actions panel.
 * Shows context-sensitive actions for the selected building.
 */

import { useEffect, useState } from 'react';
import { useBuildingSelectionStore } from '@shared/stores/buildingSelectionStore';
import { useEconomyStore } from '@shared/stores/economyStore';
import { BuildingType, isTowerType } from '@shared/constants/buildings';
import { UnitType } from '@shared/constants/units';
import { canUpgradeBuilding } from '@shared/utils/buildingUpgrade';
import ActionButton from './ActionButton';

interface BuildingActionsPanelProps {
  scene: unknown | null;
}

// Upgrade costs
const TOWER_UPGRADE_COST = 25;
const WALL_UPGRADE_COST = 25;
const MINE_UPGRADE_COST = 50;

// Training costs
const SWORDSMAN_COST = 50;
const ARCHER_COST = 75;
const HERO_COST = 200;

// Tech building research costs
const TECH_TOWER_UPGRADE_COST = 100;
const TECH_ARMOR_UPGRADE_COST = 75;
const TECH_DAMAGE_UPGRADE_COST = 75;

export default function BuildingActionsPanel({ scene }: BuildingActionsPanelProps) {
  const selectedBuildingId = useBuildingSelectionStore((state) => state.selectedBuildingId);
  const { deductCurrency, hasEnoughCurrency } = useEconomyStore();
  // Force re-render when upgrade state changes
  const [, setUpdateTrigger] = useState(0);

  // Poll for upgrade state changes to update button state immediately
  useEffect(() => {
    if (!selectedBuildingId || !scene) {
      return;
    }

    const sceneWithMethods = scene as {
      getBuildingUpgradeSystem?: () => {
        isUpgrading: (buildingId: string) => boolean;
      } | null;
    };

    const upgradeSystem = sceneWithMethods.getBuildingUpgradeSystem?.();
    if (!upgradeSystem) {
      return;
    }

    // Poll every 50ms to detect when upgrade completes (faster than BuildingSelectionDisplay's 100ms)
    // Just check the state to trigger re-render when it changes
    const interval = setInterval(() => {
      // Trigger re-render by updating state (this will cause the component to re-check isUpgrading)
      setUpdateTrigger((prev) => prev + 1);
    }, 50);

    return () => {
      clearInterval(interval);
    };
  }, [scene, selectedBuildingId]);

  if (!selectedBuildingId || !scene) {
    return null;
  }

  // Get building data from scene
  const sceneWithMethods = scene as {
    getBuildingManager?: () => {
      getBuilding: (id: string) => {
        getBuildingType: () => BuildingType;
        getLevel: () => number;
        isConstructed: () => boolean;
        setLevel: (level: number) => void;
        setHealthToMax: () => void;
        upgradeLevel: () => void;
        queueTraining: (unitType: UnitType) => boolean;
        getTrainingQueue: () => UnitType[];
        getCurrentTraining: () => UnitType | null;
        cancelLastTraining: () => boolean;
        cancelCurrentTraining: () => UnitType | null;
        cancelTrainingAtIndex: (index: number) => boolean;
      } | undefined;
    } | null;
    getUnitSpawner?: () => {
      spawnUnit: (unitType: string, x: number, y: number) => void;
    } | null;
    getBuildingUpgradeSystem?: () => {
      getMaxLevel: (buildingType: BuildingType) => number;
      enableTowerLevel5: () => void;
      isTowerLevel5Enabled: () => boolean;
      startDirectUpgrade: (buildingId: string, cost: number) => boolean;
      isUpgrading: (buildingId: string) => boolean;
      cancelUpgrade: (buildingId: string) => boolean;
      queueTechResearch: (researchType: string, cost: number) => boolean;
      getTechResearchQueue: () => string[];
      getCurrentTechResearch: () => string | null;
      cancelLastTechResearch: () => boolean;
      cancelTechResearchAtIndex: (index: number) => boolean;
      cancelCurrentTechResearch: () => boolean;
    } | null;
  };

  const buildingManager = sceneWithMethods.getBuildingManager?.();
  if (!buildingManager) return null;

  const building = buildingManager.getBuilding(selectedBuildingId);
  if (!building || !building.isConstructed()) return null;

  const buildingType = building.getBuildingType();
  const level = building.getLevel();

  // Handler for tower upgrade
  const handleTowerUpgrade = () => {
    if (!hasEnoughCurrency(TOWER_UPGRADE_COST)) return;
    
    // Check level cap using shared utility
    const upgradeSystem = sceneWithMethods.getBuildingUpgradeSystem?.();
    if (!canUpgradeBuilding(buildingType, level, upgradeSystem)) {
      return; // Already at max level
    }
    
    // Start upgrade through upgrade system (for progress tracking)
    if (upgradeSystem) {
      upgradeSystem.startDirectUpgrade(selectedBuildingId, TOWER_UPGRADE_COST);
    }
  };

  // Handler for wall upgrade
  const handleWallUpgrade = () => {
    if (!hasEnoughCurrency(WALL_UPGRADE_COST)) return;
    
    const upgradeSystem = sceneWithMethods.getBuildingUpgradeSystem?.();
    if (upgradeSystem) {
      upgradeSystem.startDirectUpgrade(selectedBuildingId, WALL_UPGRADE_COST);
    }
  };

  // Handler for mine upgrade
  const handleMineUpgrade = () => {
    if (!hasEnoughCurrency(MINE_UPGRADE_COST)) return;
    
    const upgradeSystem = sceneWithMethods.getBuildingUpgradeSystem?.();
    if (upgradeSystem) {
      upgradeSystem.startDirectUpgrade(selectedBuildingId, MINE_UPGRADE_COST);
    }
  };

  // Handler for training swordsman
  const handleTrainSwordsman = () => {
    if (!hasEnoughCurrency(SWORDSMAN_COST)) return;
    
    if (deductCurrency(SWORDSMAN_COST)) {
      // Queue training instead of spawning immediately
      const buildingWithTraining = building as unknown as {
        queueTraining: (unitType: UnitType) => boolean;
      };
      if (buildingWithTraining.queueTraining) {
        const queued = buildingWithTraining.queueTraining(UnitType.MELEE);
        if (!queued) {
          // Queue is full, refund the currency
          useEconomyStore.getState().addCurrency(SWORDSMAN_COST);
        }
      }
    }
  };

  // Handler for training archer
  const handleTrainArcher = () => {
    if (!hasEnoughCurrency(ARCHER_COST)) return;
    
    if (deductCurrency(ARCHER_COST)) {
      // Queue training instead of spawning immediately
      const buildingWithTraining = building as unknown as {
        queueTraining: (unitType: UnitType) => boolean;
      };
      if (buildingWithTraining.queueTraining) {
        const queued = buildingWithTraining.queueTraining(UnitType.RANGED);
        if (!queued) {
          // Queue is full, refund the currency
          useEconomyStore.getState().addCurrency(ARCHER_COST);
        }
      }
    }
  };



  // Handler for training hero
  const handleTrainHero = () => {
    if (!hasEnoughCurrency(HERO_COST)) return;
    // TODO: Implement hero spawning when hero unit type exists
    console.log('Hero training not yet implemented');
  };

  // Handler for tech building upgrades
  const handleTechTowerUpgrade = () => {
    if (!hasEnoughCurrency(TECH_TOWER_UPGRADE_COST)) return;
    
    const upgradeSystem = sceneWithMethods.getBuildingUpgradeSystem?.();
    if (upgradeSystem) {
      // Queue research (for queue system)
      upgradeSystem.queueTechResearch('tower_level_5', TECH_TOWER_UPGRADE_COST);
    }
  };

  const handleTechArmorUpgrade = () => {
    if (!hasEnoughCurrency(TECH_ARMOR_UPGRADE_COST)) return;
    
    const upgradeSystem = sceneWithMethods.getBuildingUpgradeSystem?.();
    if (upgradeSystem) {
      // Queue research (for queue system)
      upgradeSystem.queueTechResearch('armor_upgrade', TECH_ARMOR_UPGRADE_COST);
    }
  };

  const handleTechDamageUpgrade = () => {
    if (!hasEnoughCurrency(TECH_DAMAGE_UPGRADE_COST)) return;
    
    const upgradeSystem = sceneWithMethods.getBuildingUpgradeSystem?.();
    if (upgradeSystem) {
      // Queue research (for queue system)
      upgradeSystem.queueTechResearch('damage_upgrade', TECH_DAMAGE_UPGRADE_COST);
    }
  };

  // Render actions based on building type
  const renderActions = () => {
    // Tower actions
    if (isTowerType(buildingType)) {
      const upgradeSystem = sceneWithMethods.getBuildingUpgradeSystem?.();
      const isTowerLevel5Enabled = upgradeSystem?.isTowerLevel5Enabled() ?? false;
      const isUpgrading = upgradeSystem?.isUpgrading(selectedBuildingId) ?? false;
      const canAfford = hasEnoughCurrency(TOWER_UPGRADE_COST);
      
      // Hide button only if at level 5 (absolute max)
      if (level >= 5) {
        return null;
      }
      
      // Disable button if upgrading, or if at level 4 and level 5 not enabled
      const isDisabled = isUpgrading || (level === 4 && !isTowerLevel5Enabled);
      const disabledReason = isUpgrading
        ? ' (Upgrading...)'
        : level === 4 && !isTowerLevel5Enabled
          ? ' (Requires Tech Building upgrade "Tower Lv5")'
          : '';
      
      return (
        <div className="flex flex-wrap gap-2">
          <ActionButton
            label="Upgrade"
            hotkey="U"
            onClick={handleTowerUpgrade}
            tooltip={`Upgrade tower to Lv.${level + 1} (+10% stats) - ${TOWER_UPGRADE_COST}g${!canAfford ? ' (Not enough gold)' : disabledReason}`}
            disabled={isDisabled}
          />
        </div>
      );
    }

    // Barracks actions
    if (buildingType === BuildingType.BARRACKS) {
      const canAffordSwordsman = hasEnoughCurrency(SWORDSMAN_COST);
      const canAffordArcher = hasEnoughCurrency(ARCHER_COST);
      
      // Get training queue to check if full
      const buildingWithTraining = building as unknown as {
        getTrainingQueue: () => UnitType[];
        getCurrentTraining: () => UnitType | null;
      };
      const trainingQueue = buildingWithTraining.getTrainingQueue?.() || [];
      const queueSize = trainingQueue.length;
      const maxQueueSize = 43;
      const isQueueFull = queueSize >= maxQueueSize;
      
      return (
        <div className="flex flex-wrap gap-2">
          <ActionButton
            label="Swordsman"
            hotkey="S"
            onClick={handleTrainSwordsman}
            tooltip={`Train Swordsman - ${SWORDSMAN_COST}g${!canAffordSwordsman ? ' (Not enough gold)' : isQueueFull ? ' (Queue full)' : ''}`}
            disabled={isQueueFull}
          />
          <ActionButton
            label="Archer"
            hotkey="A"
            onClick={handleTrainArcher}
            tooltip={`Train Archer - ${ARCHER_COST}g${!canAffordArcher ? ' (Not enough gold)' : isQueueFull ? ' (Queue full)' : ''}`}
            disabled={isQueueFull}
          />
        </div>
      );
    }

    // Hero Spawner actions
    if (buildingType === BuildingType.HERO_SPAWNER) {
      const canAfford = hasEnoughCurrency(HERO_COST);
      return (
        <div className="flex flex-wrap gap-2">
          <ActionButton
            label="Train Hero"
            hotkey="H"
            onClick={handleTrainHero}
            tooltip={`Train Hero - ${HERO_COST}g${!canAfford ? ' (Not enough gold)' : ''}`}
          />
        </div>
      );
    }

    // Tech Building actions
    if (buildingType === BuildingType.TECH_BUILDING) {
      const upgradeSystem = sceneWithMethods.getBuildingUpgradeSystem?.();
      const isTowerLevel5Enabled = upgradeSystem?.isTowerLevel5Enabled() ?? false;
      const researchQueue = upgradeSystem?.getTechResearchQueue() ?? [];
      const currentResearch = upgradeSystem?.getCurrentTechResearch() ?? null;
      const totalInQueue = researchQueue.length + (currentResearch !== null ? 1 : 0);
      const maxQueueSize = 43;
      const isQueueFull = totalInQueue >= maxQueueSize;
      const canAffordTower = hasEnoughCurrency(TECH_TOWER_UPGRADE_COST);
      const canAffordArmor = hasEnoughCurrency(TECH_ARMOR_UPGRADE_COST);
      const canAffordDamage = hasEnoughCurrency(TECH_DAMAGE_UPGRADE_COST);
      
      return (
        <div className="flex flex-wrap gap-2">
          {/* Hide Tower Lv5 upgrade button if already researched */}
          {!isTowerLevel5Enabled && (
            <ActionButton
              label="Tower Lv5"
              hotkey="T"
              onClick={handleTechTowerUpgrade}
              tooltip={`Upgrade all towers to Lv.5 - ${TECH_TOWER_UPGRADE_COST}g${!canAffordTower ? ' (Not enough gold)' : isQueueFull ? ' (Queue full)' : ''}`}
              disabled={isQueueFull}
            />
          )}
          <ActionButton
            label="Armor +1"
            hotkey="A"
            onClick={handleTechArmorUpgrade}
            tooltip={`Upgrade all unit armor by 1 - ${TECH_ARMOR_UPGRADE_COST}g${!canAffordArmor ? ' (Not enough gold)' : isQueueFull ? ' (Queue full)' : ''}`}
            disabled={isQueueFull}
          />
          <ActionButton
            label="Damage +10"
            hotkey="D"
            onClick={handleTechDamageUpgrade}
            tooltip={`Upgrade all unit damage by 10 - ${TECH_DAMAGE_UPGRADE_COST}g${!canAffordDamage ? ' (Not enough gold)' : isQueueFull ? ' (Queue full)' : ''}`}
            disabled={isQueueFull}
          />
        </div>
      );
    }

    // Wall actions
    if (buildingType === BuildingType.WALL) {
      const canAfford = hasEnoughCurrency(WALL_UPGRADE_COST);
      return (
        <div className="flex flex-wrap gap-2">
          <ActionButton
            label="Upgrade"
            hotkey="W"
            onClick={handleWallUpgrade}
            tooltip={`Upgrade wall to Lv.${level + 1} (+20% health, +1 armor) - ${WALL_UPGRADE_COST}g${!canAfford ? ' (Not enough gold)' : ''}`}
          />
        </div>
      );
    }

    // Mine actions
    if (buildingType === BuildingType.MINE) {
      const canAfford = hasEnoughCurrency(MINE_UPGRADE_COST);
      const currentIncome = Math.pow(2, level - 1);
      const nextIncome = Math.pow(2, level);
      return (
        <div className="flex flex-wrap gap-2">
          <ActionButton
            label="Upgrade"
            hotkey="U"
            onClick={handleMineUpgrade}
            tooltip={`Upgrade mine to Lv.${level + 1} (${currentIncome} → ${nextIncome} income) - ${MINE_UPGRADE_COST}g${!canAfford ? ' (Not enough gold)' : ''}`}
          />
        </div>
      );
    }

    return null;
  };

  return (
    <div className="flex flex-col gap-2" data-building-actions>
      {renderActions()}
    </div>
  );
}

